import { FC, useState, useMemo, useEffect } from "react";
import { Box, Grid, Stack, styled, Typography } from "@mui/material";
import { InnerBox } from "@/components/elements/InnerBox";
import { JobFormTitle } from "@/features/job/components/job-form/JobFormTitle";
import { JobInfoItem } from "@/features/job/components/job-detail/JobInfoItem";
import { JobObjectFieldsDetail } from "@/features/job/components/job-detail/JobObjectFieldsDetail";
import { JobWorkingTimeItemDetail } from "@/features/job/components/job-detail/JobWorkingTimeItemDetail";
import { JobStatusTag } from "@/features/job/components/JobStatusTag";
import { JobTagList } from "@/features/job/components/JobTagList";
import { TJobForm, TJobUploadFile } from "@/features/job/types/JobForm";
import { convertNumberToCommas } from "@/utils/input";
import { findLabelByValue, findLabelsByValue } from "@/utils/object";
import { black, primary, state, text } from "@/theme/colors";
import { EMPTY_STR } from "@/constants/string";
import { DATE_FORMAT, YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { JOB_GENDER_OPTIONS } from "@/features/job/constants/enum";
import { NATIONALITY_ANY_VALUE, NATIONALITY_ANY_LABEL } from "@/constants/nationality";
import { SharedTalentFileItem } from "@/features/sharing/components/SharedTalentFileItem";
import { verifySharingApi } from "@/api/services/main/sharing";
import { TTalentUploadFile } from "@/features/talent/types/talentForm";
import dayjs from "dayjs";
import Divider from "@mui/material/Divider";

type SharedJobDetailProps = {
  hash: string;
  data: Record<string, unknown>;
  sharePassword?: string;
};

/** Local status label map for shared (no-auth) page — enum API requires auth */
const JOB_STATUS_LABELS: Record<string, string> = {
  draft: "下書き",
  scheduled: "募集予定",
  open: "募集中",
  paused: "募集停止",
  closed: "募集終了",
};

/** Local residence status map — enum API requires auth */
const RESIDENCE_STATUS_LABELS: Record<number, string> = {
  1: "技能実習",
  2: "特定技能",
  3: "技人国",
  4: "その他",
};

const TAB_ITEMS = [
  { value: "basic", label: "基本情報" },
  { value: "employment", label: "雇用条件" },
  { value: "working", label: "勤務条件" },
  { value: "requirement", label: "応募要件" },
  { value: "benefit", label: "福利厚生" },
  { value: "feature", label: "求人特徴" },
  { value: "files", label: "その他関連情報" },
] as const;

type TabValue = (typeof TAB_ITEMS)[number]["value"];

export const SharedJobDetail: FC<SharedJobDetailProps> = ({ hash, data, sharePassword }) => {
  // Re-verify on mount to get fresh/complete data from BE
  const [jobData, setJobData] = useState<Record<string, unknown>>(data);

  useEffect(() => {
    if (!sharePassword) return;
    verifySharingApi({ shareUrl: hash, sharePassword })
      .then((result) => {
        if (result.data?.data) {
          setJobData(result.data.data);
          // Update sessionStorage with fresh data
          const storageKey = `shared_data_${hash}`;
          const stored = sessionStorage.getItem(storageKey);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              parsed.entityData = result.data.data;
              sessionStorage.setItem(storageKey, JSON.stringify(parsed));
            } catch {
              // Ignore parse errors
            }
          }
        }
      })
      .catch(() => {
        // Fall back to initial data on error
      });
  }, [hash, sharePassword]);

  const job = jobData as unknown as Partial<TJobForm> & {
    employmentTypeName?: string;
    salaryTypeName?: string;
    bonusName?: string;
    salaryRaiseName?: string;
    japaneseLevelTypeName?: string;
    recruitmentLocationTypeName?: string;
    clientTypeName?: string;
    clientTypeNames?: string[];
    recruitmentSourceName?: string;
    recruitmentSources?: { id: string | number; recruitmentSourceName: string }[];
    genders?: number[];
    residenceStatusNames?: string | string[];
    specificSkillCertificateNames?: string | string[];
    statusName?: string;
    companySpecificBusinessClassificationNames?: string;
    shareUrl?: string;
  };

  const [activeTab, setActiveTab] = useState<TabValue>("basic");

  const nationalitiesDisplay = useMemo(() => {
    const nationalityIds = job.nationalityIds;
    const nationalities = job.nationalities;

    if (nationalityIds?.includes(String(NATIONALITY_ANY_VALUE))) {
      return NATIONALITY_ANY_LABEL;
    }

    return nationalities?.map((item) => item.nationalityName).join("、");
  }, [job.nationalityIds, job.nationalities]);

  const residenceStatusDisplay = useMemo(() => {
    if (job.residenceStatusIds?.length) {
      return job.residenceStatusIds.map((id) => RESIDENCE_STATUS_LABELS[Number(id)] ?? String(id)).join("、");
    }
    if (Array.isArray(job.residenceStatusNames)) return job.residenceStatusNames.join("、");
    return job.residenceStatusNames;
  }, [job.residenceStatusIds, job.residenceStatusNames]);

  const shareUrl = job.shareUrl ?? hash;

  return (
    <Stack direction="row" justifyContent="center">
      <Box width={1100} mt={4} pb={8} mb={2} sx={{ "& .MuiTypography-body14": { fontWeight: 500 } }}>
        {/* Title area */}
        <Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="head20Bold">{job.jobName ?? "求人情報"}</Typography>
            {job.status && (job.statusName || JOB_STATUS_LABELS[job.status]) && (
              <JobStatusTag status={job.status} title={job.statusName ?? JOB_STATUS_LABELS[job.status] ?? ""} variant="bold" />
            )}
          </Stack>
          {job.tagIds && <JobTagList tagIds={job.tagIds} createdAt={job.createdAt} />}
          <Stack direction="row" gap={2} mt={1}>
            <Typography variant="body14" color={text.secondary}>
              登録日: {job.createdAt ? dayjs(job.createdAt).format(DATE_FORMAT) : ""}
            </Typography>
            <Typography variant="body14" color={text.secondary}>
              更新日: {job.updatedAt ? dayjs(job.updatedAt).format(DATE_FORMAT) : ""}
            </Typography>
          </Stack>
        </Stack>

        {/* Tab navigation — anchor scroll to sections */}
        <TabContainer mt={3} width="1100">
          {TAB_ITEMS.map((item) => (
            <TabButton
              key={item.value}
              type="button"
              onClick={() => setActiveTab(item.value)}
              $isActive={activeTab === item.value}
              sx={{
                width: "150px",
                height: "56px",
                marginRight: "8px",
              }}
            >
              <Typography variant="sub16Bold">{item.label}</Typography>
            </TabButton>
          ))}
        </TabContainer>

        {/* Tab content */}
        <Stack gap={3}>
          {activeTab === "basic" && (
            <InnerBox title={<JobFormTitle text="基本情報" />} padding="24px" showDivider={false}>
              <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
                <Grid item xs={3}>
                  <JobInfoItem label="企業名" value={job.company?.companyName} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="求人管理番号" value={job.managementNumber} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="求人名" value={job.jobName} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="分野" value={job.companySpecificIndustry?.companySpecificIndustry} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="業務区分" value={job.companySpecificBusinessClassificationNames} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="取扱業務等の区分" value={job.businessClassification?.businessCategory} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="業務内容" value={job.businessContent} />
                </Grid>

                <Grid item xs={3}>
                  <JobInfoItem
                    label="募集人数"
                    value={job.recruitmentNumber !== null && job.recruitmentNumber !== undefined ? `${job.recruitmentNumber}人` : ""}
                  />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem
                    label="紹介料（売上）"
                    value={job.referralFee !== null && job.referralFee !== undefined ? `${convertNumberToCommas(job.referralFee.toString())}円` : ""}
                  />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem
                    label="紹介料（仕入）"
                    value={
                      job.referralFeeCost !== null && job.referralFeeCost !== undefined
                        ? `${convertNumberToCommas(job.referralFeeCost.toString())}円`
                        : ""
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem
                    label="求人獲得ルート"
                    value={
                      job.recruitmentSources?.map((s) => s.recruitmentSourceName).join("、") ??
                      job.recruitmentSourceName ??
                      job.recruitmentSource?.recruitmentSourceName
                    }
                  />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="顧客区分" value={job.clientTypeNames?.join("、") ?? job.clientTypeName} />
                </Grid>
                <Grid item xs={12}>
                  <JobInfoItem label="求人担当者コメント" value={job.recruiterNote} isShowDivider={false} />
                </Grid>
              </Grid>
            </InnerBox>
          )}

          {activeTab === "employment" && (
            <InnerBox title={<JobFormTitle text="雇用条件" />} padding="24px" showDivider={false}>
              <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
                <Grid item xs={4}>
                  <JobInfoItem label="業務内容" value={job.businessContent} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="雇用形態" value={job.employmentTypeName ?? job.employmentType} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="給与形態" value={job.salaryTypeName ?? job.salaryType} />
                </Grid>
                {job.jobSalarySummaries?.map((summary, index) => (
                  <Grid item xs={12} key={index}>
                    <InnerBox title={`総支給額${index + 1}`} padding="16px" showDivider={false}>
                      <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
                        <Grid item xs={4}>
                          <JobInfoItem
                            label="総支給額"
                            value={
                              summary.totalAmount !== null && summary.totalAmount !== undefined
                                ? `${convertNumberToCommas(summary.totalAmount.toString())}円`
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <JobInfoItem label="総支給額の内訳" value={summary.totalAmountBreakdown} />
                        </Grid>
                        <Grid item xs={4}>
                          <JobInfoItem
                            label="控除金額"
                            value={
                              summary.deductionAmount !== null && summary.deductionAmount !== undefined
                                ? `${convertNumberToCommas(summary.deductionAmount.toString())}円`
                                : ""
                            }
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <JobInfoItem label="控除内容の詳細" value={summary.deductionDetail} isShowDivider={false} />
                        </Grid>
                        <Grid item xs={4}>
                          <JobInfoItem
                            label="目安手取り金額"
                            value={
                              summary.netSalaryEstimate !== null && summary.netSalaryEstimate !== undefined
                                ? `${convertNumberToCommas(summary.netSalaryEstimate.toString())}円`
                                : ""
                            }
                            isShowDivider={false}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <JobInfoItem label="手取り金額計算背景" value={summary.netSalaryEstimateNote} isShowDivider={false} />
                        </Grid>
                      </Grid>
                    </InnerBox>
                  </Grid>
                ))}
                <Grid item xs={3}>
                  <JobInfoItem label="その他手当の内容" value={job.otherAllowanceDescription} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="残業に関する説明" value={job.overtimeDescription} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="賞与" value={job.bonusName ?? job.bonus} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="賞与時期・金額" value={job.bonusInfo} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="昇給" value={job.salaryRaiseName ?? job.salaryRaise} isShowDivider={false} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="昇給時期" value={job.salaryRaiseTime} isShowDivider={false} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="雇用条件に関する補足事項" value={job.employmentConditionNote} isShowDivider={false} />
                </Grid>
              </Grid>
            </InnerBox>
          )}

          {activeTab === "working" && (
            <InnerBox title={<JobFormTitle text="勤務条件" />} padding="24px" showDivider={false}>
              <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
                <Grid item xs={6}>
                  <JobInfoItem label="勤務場所" value={job.workplace} />
                </Grid>
                <Grid item xs={6}>
                  <JobInfoItem label="勤務地（詳細）" value={job.workplaceDetail} />
                </Grid>
                <Grid item xs={12}>
                  {job.jobWorkingTimes?.map((workingTime, index) => (
                    <JobWorkingTimeItemDetail key={index} title={`労働時間${index + 1}`} workingTime={workingTime} showDivider={false} />
                  ))}
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="休日（定例日）" value={job.regularHolidays} isShowDivider={false} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="休日（非定例日）" value={job.irregularHolidays} isShowDivider={false} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem
                    label="年間合計休日日数"
                    value={
                      job.totalAnnualHolidays !== null && job.totalAnnualHolidays !== undefined ? `${job.totalAnnualHolidays?.toString()}日` : ""
                    }
                    isShowDivider={false}
                  />
                </Grid>
              </Grid>
            </InnerBox>
          )}

          {activeTab === "requirement" && (
            <InnerBox title={<JobFormTitle text="応募要件" />} padding="24px" showDivider={false}>
              <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
                <Grid item xs={3}>
                  <JobInfoItem label="必要な日本語レベル" value={job.japaneseLevelTypeName ?? job.japaneseLevelType?.toUpperCase()} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="必要な日本語レベル（その他）" value={job.japaneseLevelOther} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="必要な技能・経験" value={job.skillsExperience} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="その他応募要件" value={job.otherApplicationRequirements} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="年齢制限（上限）" value={job.ageLimit} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="国籍" value={nationalitiesDisplay} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="募集地" value={job.recruitmentLocationTypeName ?? job.recruitmentLocationType} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="採用希望時期" value={job.desiredHiringPeriod} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem
                    label="性別"
                    value={
                      job.genders?.length
                        ? findLabelsByValue(JOB_GENDER_OPTIONS, job.genders.map(String))
                        : findLabelByValue(JOB_GENDER_OPTIONS, job.gender)
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="在留資格" value={residenceStatusDisplay} />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem
                    label="特定技能試験合格"
                    value={
                      Array.isArray(job.specificSkillCertificateNames)
                        ? job.specificSkillCertificateNames.join("、")
                        : job.specificSkillCertificateNames
                    }
                  />
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem
                    label="求人票（URL）"
                    value={job.jobDocumentUrl}
                    {...(job.jobDocumentUrl ? { color: state.link_1, type: "link" } : {})}
                    isShowDivider={false}
                  />
                </Grid>
                <Grid item xs={4}>
                  <Stack gap={1}>
                    <Typography variant="cap12Light" color={black[70]}>
                      求人票（ファイル添付）
                    </Typography>
                    {job.jobDocumentFilePaths?.length ? (
                      <Stack direction="column" spacing={2}>
                        {job.jobDocumentFilePaths.map((path, index) => (
                          <SharedTalentFileItem
                            key={index}
                            filePath={path}
                            shareUrl={shareUrl}
                            files={job.jobDocumentFilePaths!.map((p) => ({ filePath: p })) as TTalentUploadFile[]}
                            currentFileIndex={index}
                          />
                        ))}
                      </Stack>
                    ) : (
                      <>
                        <Typography variant="body14">{EMPTY_STR.TEXT}</Typography>
                        <Divider sx={{ mt: 1, width: "100%" }} />
                      </>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <JobInfoItem label="選考フロー" value={job.selectionProcess} isShowDivider={false} />
                </Grid>
              </Grid>
            </InnerBox>
          )}

          {activeTab === "benefit" && (
            <InnerBox title={<JobFormTitle text="福利厚生" />} padding="24px" showDivider={false}>
              <Grid container spacing={2} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
                <Grid item xs={3}>
                  <JobInfoItem label="社宅・住居情報" value={job.companyAccommodation} isShowDivider={false} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="特定技能2号支援" value={job.specifiedSkillNo2} isShowDivider={false} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="その他福利厚生や宗教面配慮の有無等" value={job.otherBenefits} isShowDivider={false} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="社会保険・雇用保険の加入状況" value={job.insuranceStatus} isShowDivider={false} />
                </Grid>
              </Grid>
            </InnerBox>
          )}

          {activeTab === "feature" && (
            <InnerBox title={<JobFormTitle text="求人特徴" />} padding="24px" showDivider={false}>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <JobObjectFieldsDetail title="本人負担費用" data={job.selfExpense} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="求人企業の特徴（選考ポイント）" value={job.companyFeatures} isShowDivider={false} />
                </Grid>
                <Grid item xs={3}>
                  <JobInfoItem label="在籍外国人" value={job.foreignEmployees} isShowDivider={false} />
                </Grid>
                <Grid item xs={3}>
                  <JobObjectFieldsDetail title="受け入れ体制・サポート" data={job.onboardingSupport} />
                </Grid>
              </Grid>
            </InnerBox>
          )}

          {activeTab === "files" && (
            <InnerBox title={<JobFormTitle text="その他関連情報" />} padding="24px" showDivider={false}>
              {job.jobUploadFiles?.length ? (
                <Stack>
                  {job.jobUploadFiles.map((item: TJobUploadFile, idx: number) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mt: 1,
                      }}
                    >
                      <SharedTalentFileItem
                        filePath={item.filePath}
                        shareUrl={shareUrl}
                        title={item.title}
                        files={job.jobUploadFiles!.map((f) => ({ filePath: f.filePath, updatedAt: f.createdAt })) as TTalentUploadFile[]}
                        currentFileIndex={idx}
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" color={text.secondary} sx={{ whiteSpace: "nowrap", pl: 1 }}>
                          {item.createdAt ? dayjs(item.createdAt).format(YEAR_MONTH_DAY_STR_FORMAT) : ""} 更新
                        </Typography>
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography>{EMPTY_STR.TEXT}</Typography>
              )}
            </InnerBox>
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

const TabContainer = styled(Box)`
  display: flex;
  gap: 4px;
  border-bottom: 4px solid ${primary[500]};
  padding-bottom: 0;
`;

const TabButton = styled("button")<{ $isActive: boolean }>`
  border: none;
  border-radius: 8px 8px 0 0;
  height: 40px;
  padding: 8px 20px;
  background-color: ${({ $isActive }) => ($isActive ? primary[500] : "#CACDCD")};
  color: white;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;

  &:focus {
    outline: none;
  }
`;
